import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { criticModelsService } from '../../services/criticModelsService';
import { CriticShortlistRow } from '../../types/articleGeneration';
import ModelSelect from '../CriticModels/ModelSelect';
import { isModelAllowed } from '../../utils/aiProviders';

const SLOTS = [1, 2, 3, 4, 5];
const DEFAULT_WRITER = 'openai/gpt-4o';

interface Props {
  companyId: string;
  /** Company admins and super admins edit; members read. */
  canEdit: boolean;
  /** The company's OpenRouter key: article generation uses it too. */
  companyKey?: string | null;
  /** Providers a super admin allowed for this company; null = all. */
  allowedProviders?: string[] | null;
}

/**
 * Article generation models of one company: the writer model and the five
 * critic slots. Every read and write is scoped to `companyId` (and to the
 * company's members by RLS), so companies never see or change each other's
 * models. The OpenRouter key is the company key edited above.
 */
const ArticleModelsSection: React.FC<Props> = ({ companyId, canEdit, companyKey, allowedProviders }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [models, setModels] = useState<{ id: string; name: string }[]>([]);
  const [shortlist, setShortlist] = useState<CriticShortlistRow[]>([]);
  const [writerModel, setWriterModel] = useState(DEFAULT_WRITER);
  const [savedWriterModel, setSavedWriterModel] = useState(DEFAULT_WRITER);
  const [newModelId, setNewModelId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [loading, setLoading] = useState(true);

  const fail = (error: unknown) =>
    showNotification(error instanceof Error ? error.message : String(error), 'error');

  const reload = async () => {
    const [rows, settings] = await Promise.all([
      criticModelsService.listShortlist(companyId),
      criticModelsService.getSettings(companyId),
    ]);
    setShortlist(rows);
    const model = settings?.writer_model_id || DEFAULT_WRITER;
    setWriterModel(model);
    setSavedWriterModel(model);
  };

  useEffect(() => {
    criticModelsService.listModels().then(setModels).catch(() => setModels([]));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    reload()
      .catch((e) => active && fail(e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [companyId]);

  const allowedModels = models.filter((m) => isModelAllowed(m.id, allowedProviders));
  const writerBlocked = !isModelAllowed(writerModel, allowedProviders);
  const assigned = shortlist.filter((r) => r.critic_slot != null).length;
  const keyConfigured = (companyKey ?? '').trim() !== '';
  const configured = assigned === 5 && keyConfigured;
  const dirty = writerModel !== savedWriterModel;

  const saveSettings = async () => {
    try {
      await criticModelsService.upsertSettings(companyId, { writer_model_id: writerModel });
      setSavedWriterModel(writerModel);
      showNotification(t('critics.settings_saved'), 'success');
    } catch (e) {
      fail(e);
    }
  };

  const addModel = async () => {
    if (!newModelId || !newLabel.trim()) return;
    try {
      await criticModelsService.addToShortlist(companyId, newModelId, newLabel.trim());
      setNewModelId('');
      setNewLabel('');
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  const assign = async (row: CriticShortlistRow, slot: number | null) => {
    try {
      await criticModelsService.assignSlot(companyId, row.id, slot);
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  const remove = async (row: CriticShortlistRow) => {
    try {
      await criticModelsService.removeFromShortlist(row.id);
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  return (
    <section data-testid="article-models-section" className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{t('critics.section_title')}</h4>
        <p className="text-xs text-gray-500">{t('critics.section_hint')}</p>
      </div>

      {!loading && !configured && (
        <div className="p-3 border border-yellow-300 bg-yellow-50 rounded-md text-sm">
          {t('critics.not_configured_warning')}
        </div>
      )}

      {/* Writer model */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">{t('critics.writer_model')}</div>
          {canEdit ? (
            <ModelSelect models={allowedModels} value={writerModel} onChange={setWriterModel} />
          ) : (
            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 font-mono">
              {writerModel}
            </span>
          )}
          <p className="mt-1 text-xs text-gray-500">{t('critics.writer_model_hint')}</p>
          {writerBlocked && (
            <p className="mt-1 text-xs text-amber-700" data-testid="writer-model-blocked">
              {t('critics.model_blocked')}
            </p>
          )}
        </div>
      </div>
      {canEdit && (
        <button
          type="button"
          onClick={saveSettings}
          disabled={!dirty}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-semibold disabled:bg-gray-300"
        >
          {t('critics.save_settings')}
        </button>
      )}

      {/* Critic slots */}
      <div>
        <div className="text-sm font-medium text-gray-700 mb-2">{t('critics.slots_title')}</div>
        <div className="flex gap-2 flex-wrap mb-3">
          {SLOTS.map((slot) => {
            const row = shortlist.find((r) => r.critic_slot === slot);
            return (
              <span
                key={slot}
                data-testid={`critic-slot-${slot}`}
                className={`px-3 py-1.5 border rounded-full text-sm ${row ? 'bg-white' : 'bg-gray-50 text-gray-500'}`}
              >
                {slot}: {row ? row.label : t('critics.slot_empty')}
              </span>
            );
          })}
        </div>

        {canEdit && (
          <div className="flex gap-3 items-end mb-3 flex-wrap">
            <div className="flex-grow min-w-[16rem]">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('critics.model_id')}</label>
              <ModelSelect models={allowedModels} value={newModelId} onChange={setNewModelId} />
            </div>
            <div className="min-w-[12rem]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('critics.label')}
                <input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="mt-1 w-full p-2 border rounded-md text-sm font-normal"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={addModel}
              disabled={!newModelId || !newLabel.trim()}
              className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold disabled:bg-gray-300"
            >
              {t('critics.add_model')}
            </button>
          </div>
        )}

        {shortlist.length > 0 && (
          <table className="w-full text-sm border rounded-md">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b">
                <th className="p-2">{t('critics.label')}</th>
                <th className="p-2">{t('critics.model_id')}</th>
                <th className="p-2">{t('critics.critic_slot')}</th>
                {canEdit && <th className="p-2"></th>}
              </tr>
            </thead>
            <tbody>
              {shortlist.map((row) => (
                <tr key={row.id} className="border-b last:border-0" data-testid="critic-row">
                  <td className="p-2">{row.label}</td>
                  <td className="p-2 font-mono text-xs">
                    {row.model_id}
                    {!isModelAllowed(row.model_id, allowedProviders) && (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-sans text-amber-800">
                        {t('critics.model_blocked_tag')}
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    {canEdit ? (
                      <select
                        aria-label={`${t('critics.critic_slot')} ${row.label}`}
                        value={row.critic_slot ?? ''}
                        onChange={(e) =>
                          assign(row, e.target.value === '' ? null : Number(e.target.value))
                        }
                        className="p-1 border rounded-md"
                      >
                        <option value="">{t('critics.no_slot')}</option>
                        {SLOTS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    ) : (
                      (row.critic_slot ?? t('critics.no_slot'))
                    )}
                  </td>
                  {canEdit && (
                    <td className="p-2 text-right">
                      <button
                        type="button"
                        onClick={() => remove(row)}
                        aria-label={`${t('critics.remove')} ${row.label}`}
                        className="text-red-600 hover:underline"
                      >
                        {t('critics.remove')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default ArticleModelsSection;
